import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

/**
 * Small inline SVG charts for the Engagement Center (mockup style). Deliberately no chart library:
 * the shapes are simple, the charts must render without a DOM (tests use static markup), and a
 * single trend line does not justify pulling echarts into the admin bundle path.
 */

const PRIMARY = '#4F44FF';
const GRID = '#E6E6EE';
const MUTED = '#707070';

interface TrendPoint {
  date: string;
  value: number;
}

interface TrendChartProps {
  points: TrendPoint[];
  annotations?: { date: string; label: string }[];
  height?: number;
  ariaLabel: string;
}

export const TrendChart: React.FC<TrendChartProps> = ({ points, annotations = [], height = 160, ariaLabel }) => {
  const width = 640;
  const pad = { l: 32, r: 8, t: 12, b: 22 };
  if (points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.value));
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const x = (i: number) => pad.l + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v: number) => pad.t + innerH - (v / max) * innerH;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${path} L${x(points.length - 1).toFixed(1)},${pad.t + innerH} L${x(0).toFixed(1)},${pad.t + innerH} Z`;
  const ticks = [0, Math.round(max / 2), max];
  const byDate = new Map(points.map((p, i) => [p.date.slice(0, 10), i]));
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={'100%'} role={'img'} aria-label={ariaLabel} style={{ display: 'block' }}>
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={pad.l} x2={width - pad.r} y1={y(tick)} y2={y(tick)} stroke={GRID} strokeWidth={1} />
          <text x={pad.l - 6} y={y(tick) + 4} fontSize={10} textAnchor={'end'} fill={MUTED}>
            {tick}
          </text>
        </g>
      ))}
      <path d={area} fill={PRIMARY} opacity={0.08} />
      <path d={path} fill={'none'} stroke={PRIMARY} strokeWidth={2} />
      {points.map((p, i) =>
        i % labelEvery === 0 || i === points.length - 1 ? (
          <text key={p.date} x={x(i)} y={height - 6} fontSize={10} textAnchor={'middle'} fill={MUTED}>
            {p.date.slice(8, 10)}.{p.date.slice(5, 7)}.
          </text>
        ) : null
      )}
      {annotations.map((a) => {
        const i = byDate.get(a.date.slice(0, 10));
        if (i === undefined) return null;
        return (
          <g key={`${a.date}-${a.label}`}>
            <title>{a.label}</title>
            <line x1={x(i)} x2={x(i)} y1={pad.t} y2={pad.t + innerH} stroke={'#FFA502'} strokeDasharray={'3 3'} />
            <polygon points={`${x(i) - 5},${pad.t + innerH + 8} ${x(i) + 5},${pad.t + innerH + 8} ${x(i)},${pad.t + innerH + 1}`} fill={'#FFA502'} />
          </g>
        );
      })}
    </svg>
  );
};

interface BarRow {
  label: string;
  value: number;
  hint?: string;
}

/** Horizontal bars (funnel, histogram buckets, churn reasons). Widths relative to the largest row. */
export const HBarList: React.FC<{ rows: BarRow[]; format?: (n: number) => string; color?: string }> = ({
  rows,
  format = (n) => String(n),
  color = PRIMARY,
}) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Stack gap={1}>
      {rows.map((r) => (
        <Stack key={r.label} direction={'row'} alignItems={'center'} gap={1.5}>
          <Typography sx={{ width: 160, flexShrink: 0, fontSize: 13 }}>{r.label}</Typography>
          <Box sx={{ flex: 1, bgcolor: '#F2F2F7', borderRadius: 1, height: 18, position: 'relative' }}>
            <Box sx={{ width: `${(r.value / max) * 100}%`, bgcolor: color, height: '100%', borderRadius: 1, minWidth: r.value > 0 ? 2 : 0 }} />
          </Box>
          <Typography sx={{ width: 90, flexShrink: 0, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {format(r.value)}
            {r.hint ? (
              <Typography component={'span'} sx={{ fontSize: 11, color: 'text.secondary', ml: 0.5 }}>
                {r.hint}
              </Typography>
            ) : null}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

/** Vertical histogram with labelled buckets (sends per user and week: 0,1,2,3,4+). */
export const Histogram: React.FC<{ buckets: { label: string; value: number }[]; ariaLabel: string }> = ({ buckets, ariaLabel }) => {
  const width = 320;
  const height = 140;
  const pad = { l: 8, r: 8, t: 16, b: 20 };
  const max = Math.max(1, ...buckets.map((b) => b.value));
  const bw = (width - pad.l - pad.r) / Math.max(1, buckets.length);
  const innerH = height - pad.t - pad.b;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={'100%'} style={{ maxWidth: 420, display: 'block' }} role={'img'} aria-label={ariaLabel}>
      {buckets.map((b, i) => {
        const h = (b.value / max) * innerH;
        const x = pad.l + i * bw + 6;
        return (
          <g key={b.label}>
            <rect x={x} y={pad.t + innerH - h} width={bw - 12} height={h} rx={3} fill={i >= 3 ? '#FF0A71' : PRIMARY} opacity={0.85} />
            <text x={x + (bw - 12) / 2} y={pad.t + innerH - h - 4} fontSize={10} textAnchor={'middle'} fill={'#333'}>
              {b.value}
            </text>
            <text x={x + (bw - 12) / 2} y={height - 6} fontSize={10} textAnchor={'middle'} fill={MUTED}>
              {b.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/** Cohort heatmap: rows = start month, columns = months after; null = future (empty cell). */
export const CohortHeatmap: React.FC<{ cohorts: { month: string; retention: (number | null)[] }[]; format: (n: number) => string }> = ({
  cohorts,
  format,
}) => {
  const cols = Math.max(0, ...cohorts.map((c) => c.retention.length));
  const cellStyle: React.CSSProperties = { padding: '6px 8px', fontSize: 12, textAlign: 'center', minWidth: 48 };
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
        <thead>
          <tr>
            <th style={{ ...cellStyle, textAlign: 'left' }} />
            {Array.from({ length: cols }, (_, i) => (
              <th key={i} style={{ ...cellStyle, color: MUTED, fontWeight: 500 }}>
                M{i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.month}>
              <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 500 }}>{c.month}</td>
              {Array.from({ length: cols }, (_, i) => {
                const v = c.retention[i];
                if (v === null || v === undefined) return <td key={i} style={{ ...cellStyle, background: '#FAFAFC' }} />;
                const ratio = v > 1 ? v / 100 : v;
                return (
                  <td
                    key={i}
                    style={{
                      ...cellStyle,
                      background: `rgba(79, 68, 255, ${(0.1 + ratio * 0.8).toFixed(2)})`,
                      color: ratio > 0.55 ? '#FFF' : '#1A1A2E',
                      borderRadius: 4,
                    }}
                  >
                    {format(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};
